// @ts-nocheck
/**
 * Structural validation for template.graphWiringDefaults and element.graphWiringDefaultsId (DEC-046).
 */

import type { ValidationResult } from '../../diagnostics';
import { addIssue } from '../../diagnostics';

const VALID_WIRING_MODES = new Set(['local', 'create-item', 'link-existing']);
const VALID_DASH_TYPES = new Set(['solid', 'dashed', 'dotted']);
const VALID_CAP_TYPES = new Set([
  'none',
  'arrow-filled',
  'arrow-open',
  'circle-filled',
  'circle-open',
  'diamond-filled',
  'diamond-open',
]);

const MIN_STROKE_WIDTH = 1;
const MAX_STROKE_WIDTH = 20;
const MIN_HANDLE_RADIUS = 1;
const MAX_HANDLE_RADIUS = 24;
const MIN_HANDLE_STROKE_WIDTH = 0;
const MAX_HANDLE_STROKE_WIDTH = 10;
const MIN_LABEL_SIZE = 6;
const MAX_LABEL_SIZE = 40;

const COLOR_PATTERN = /^#([0-9a-fA-F]{3,4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function isValidColor(value: unknown): boolean {
  return typeof value === 'string' && COLOR_PATTERN.test(value.trim());
}

function validateOptionalColor(
  result: ValidationResult,
  value: unknown,
  path: string,
  fieldLabel: string,
): ValidationResult {
  if (value === undefined) return result;
  if (!isValidColor(value)) {
    return addIssue(result, {
      severity: 'error',
      code: 'schema.invalid_field_type',
      message: `${fieldLabel} must be a hex color string`,
      path,
    });
  }
  return result;
}

function validateOptionalNumberInRange(
  result: ValidationResult,
  value: unknown,
  path: string,
  fieldLabel: string,
  min: number,
  max: number,
): ValidationResult {
  if (value === undefined) return result;
  if (typeof value !== 'number' || !Number.isFinite(value) || value < min || value > max) {
    return addIssue(result, {
      severity: 'error',
      code: 'schema.invalid_field_type',
      message: `${fieldLabel} must be a number between ${min} and ${max}`,
      path,
    });
  }
  return result;
}

function validateHandleDefaults(
  handles: unknown,
  basePath: string,
  result: ValidationResult,
): ValidationResult {
  if (handles === undefined) return result;
  if (!isPlainObject(handles)) {
    return addIssue(result, {
      severity: 'error',
      code: 'schema.invalid_field_type',
      message: 'Graph wiring defaults handles must be a plain object',
      path: basePath,
    });
  }

  let next = result;
  const h = handles;
  next = validateOptionalColor(next, h.dragHandleColor, `${basePath}.dragHandleColor`, 'dragHandleColor');
  next = validateOptionalColor(next, h.wiringHandleFillColor, `${basePath}.wiringHandleFillColor`, 'wiringHandleFillColor');
  next = validateOptionalColor(next, h.wiringHandleStrokeColor, `${basePath}.wiringHandleStrokeColor`, 'wiringHandleStrokeColor');
  next = validateOptionalNumberInRange(
    next,
    h.wiringHandleStrokeWidth,
    `${basePath}.wiringHandleStrokeWidth`,
    'wiringHandleStrokeWidth',
    MIN_HANDLE_STROKE_WIDTH,
    MAX_HANDLE_STROKE_WIDTH,
  );
  next = validateOptionalNumberInRange(
    next,
    h.wiringHandleRadius,
    `${basePath}.wiringHandleRadius`,
    'wiringHandleRadius',
    MIN_HANDLE_RADIUS,
    MAX_HANDLE_RADIUS,
  );
  return next;
}

function validateEdgeDefaults(
  edge: unknown,
  basePath: string,
  result: ValidationResult,
): ValidationResult {
  if (edge === undefined) return result;
  if (!isPlainObject(edge)) {
    return addIssue(result, {
      severity: 'error',
      code: 'schema.invalid_field_type',
      message: 'Graph wiring defaults edge must be a plain object',
      path: basePath,
    });
  }

  let next = result;
  const e = edge;
  next = validateOptionalColor(next, e.color, `${basePath}.color`, 'edge.color');
  next = validateOptionalNumberInRange(
    next,
    e.strokeWidth,
    `${basePath}.strokeWidth`,
    'edge.strokeWidth',
    MIN_STROKE_WIDTH,
    MAX_STROKE_WIDTH,
  );

  if (e.dash !== undefined && (typeof e.dash !== 'string' || !VALID_DASH_TYPES.has(e.dash))) {
    next = addIssue(next, {
      severity: 'error',
      code: 'schema.invalid_field_type',
      message: `edge.dash must be one of: ${[...VALID_DASH_TYPES].join(', ')}`,
      path: `${basePath}.dash`,
    });
  }

  for (const capField of ['startCap', 'endCap'] as const) {
    const capValue = e[capField];
    if (capValue !== undefined && (typeof capValue !== 'string' || !VALID_CAP_TYPES.has(capValue))) {
      next = addIssue(next, {
        severity: 'error',
        code: 'schema.invalid_field_type',
        message: `edge.${capField} must be a valid cap type`,
        path: `${basePath}.${capField}`,
      });
    }
  }

  return next;
}

function validateLabelDefaults(
  labels: unknown,
  basePath: string,
  result: ValidationResult,
): ValidationResult {
  if (labels === undefined) return result;
  if (!isPlainObject(labels)) {
    return addIssue(result, {
      severity: 'error',
      code: 'schema.invalid_field_type',
      message: 'Graph wiring defaults labels must be a plain object',
      path: basePath,
    });
  }

  let next = result;
  for (const field of ['startFontSize', 'midFontSize', 'endFontSize'] as const) {
    next = validateOptionalNumberInRange(
      next,
      labels[field],
      `${basePath}.${field}`,
      field,
      MIN_LABEL_SIZE,
      MAX_LABEL_SIZE,
    );
  }
  return next;
}

export function validateGraphWiringDefaultsCatalog(
  catalog: unknown,
  result: ValidationResult,
): ValidationResult {
  if (catalog === undefined) return result;

  if (!Array.isArray(catalog)) {
    return addIssue(result, {
      severity: 'error',
      code: 'schema.invalid_field_type',
      message: 'graphWiringDefaults must be an array',
      path: 'graphWiringDefaults',
    });
  }

  let next = result;
  const seenIds = new Set<string>();

  for (let i = 0; i < catalog.length; i++) {
    const entryPath = `graphWiringDefaults[${i}]`;
    const entry = catalog[i];

    if (!isPlainObject(entry)) {
      next = addIssue(next, {
        severity: 'error',
        code: 'schema.invalid_field_type',
        message: 'Graph wiring defaults entry must be a plain object',
        path: entryPath,
      });
      continue;
    }

    const row = entry;
    if (typeof row.id !== 'string' || row.id.trim() === '') {
      next = addIssue(next, {
        severity: 'error',
        code: 'schema.missing_required_field',
        message: "Graph wiring defaults entry is missing required field 'id'",
        path: `${entryPath}.id`,
      });
    } else if (seenIds.has(row.id)) {
      next = addIssue(next, {
        severity: 'error',
        code: 'schema.duplicate_id',
        message: `Duplicate graph wiring defaults id '${row.id}'`,
        path: `${entryPath}.id`,
      });
    } else {
      seenIds.add(row.id);
    }

    next = validateHandleDefaults(row.handles, `${entryPath}.handles`, next);
    next = validateEdgeDefaults(row.edge, `${entryPath}.edge`, next);
    next = validateLabelDefaults(row.labels, `${entryPath}.labels`, next);

    if (row.defaultWiringMode !== undefined) {
      if (typeof row.defaultWiringMode !== 'string' || !VALID_WIRING_MODES.has(row.defaultWiringMode)) {
        next = addIssue(next, {
          severity: 'error',
          code: 'schema.invalid_field_type',
          message: `defaultWiringMode must be one of: ${[...VALID_WIRING_MODES].join(', ')}`,
          path: `${entryPath}.defaultWiringMode`,
        });
      }
    }
  }

  return next;
}

export function validateElementGraphWiringFields(
  elements: unknown[],
  result: ValidationResult,
): ValidationResult {
  let next = result;
  const seenLevels = new Map<number, string>();

  for (let i = 0; i < elements.length; i++) {
    const element = elements[i];
    if (!isPlainObject(element)) continue;

    const elPath = `elements[${i}]`;
    const elementId = typeof element.id === 'string' ? element.id : `index-${i}`;

    if (
      element.autoPlaceConnectedItem !== undefined
      && typeof element.autoPlaceConnectedItem !== 'boolean'
    ) {
      next = addIssue(next, {
        severity: 'error',
        code: 'schema.invalid_field_type',
        message: 'autoPlaceConnectedItem must be a boolean when present',
        path: `${elPath}.autoPlaceConnectedItem`,
      });
    }

    const level = element.graphWiringCreateLevel;
    if (level === undefined) continue;

    if (
      typeof level !== 'number'
      || !Number.isInteger(level)
      || !Number.isFinite(level)
      || level < 0
    ) {
      next = addIssue(next, {
        severity: 'error',
        code: 'schema.invalid_field_type',
        message: 'graphWiringCreateLevel must be a non-negative integer when present',
        path: `${elPath}.graphWiringCreateLevel`,
      });
      continue;
    }

    const priorElementId = seenLevels.get(level);
    if (priorElementId !== undefined) {
      next = addIssue(next, {
        severity: 'error',
        code: 'schema.duplicate_graph_wiring_create_level',
        message: `Duplicate graphWiringCreateLevel ${level} on elements '${priorElementId}' and '${elementId}'`,
        path: `${elPath}.graphWiringCreateLevel`,
      });
    } else {
      seenLevels.set(level, elementId);
    }
  }

  return next;
}

export function validateElementGraphWiringDefaultsRefs(
  elements: unknown[],
  catalogIds: ReadonlySet<string>,
  result: ValidationResult,
): ValidationResult {
  let next = result;

  for (let i = 0; i < elements.length; i++) {
    const element = elements[i];
    if (!isPlainObject(element)) continue;

    const defaultsId = element.graphWiringDefaultsId;
    if (defaultsId === undefined) continue;

    const elPath = `elements[${i}]`;
    if (typeof defaultsId !== 'string' || defaultsId.trim() === '') {
      next = addIssue(next, {
        severity: 'error',
        code: 'schema.invalid_field_type',
        message: 'graphWiringDefaultsId must be a non-empty string when present',
        path: `${elPath}.graphWiringDefaultsId`,
      });
      continue;
    }

    if (!catalogIds.has(defaultsId)) {
      next = addIssue(next, {
        severity: 'error',
        code: 'schema.unresolved_reference',
        message: `Element references unknown graphWiringDefaultsId '${defaultsId}'`,
        path: `${elPath}.graphWiringDefaultsId`,
      });
    }
  }

  return next;
}

export function collectGraphWiringDefaultsIds(catalog: unknown): Set<string> {
  const ids = new Set<string>();
  if (!Array.isArray(catalog)) return ids;

  for (const entry of catalog) {
    if (isPlainObject(entry) && typeof entry.id === 'string' && entry.id.trim() !== '') {
      ids.add(entry.id);
    }
  }

  return ids;
}
